import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { numberStyle, Pagination } from '../components/AssetBits'
import { Hint } from '../components/Hint'
import { InfoDot } from '../components/InfoDot'
import { DownloadButton } from '../components/DownloadButton'
import { RevenueChart } from '../components/RevenueChart'
import { RevenueSheet } from '../components/RevenueSheet'
import { Button, Card, Note, Skeleton } from '../components/ui'
import {
  api,
  ApiError,
  type RevenueAuditRow,
  type RevenueGrid,
  type RevenueGridChannel,
  type RevenueHistoryRow,
  type RevenueQuery,
} from '../lib/api'
import { formatDate } from '../lib/dates'
import { type LanguageGroup, useFilters } from '../lib/filters'
import { formatMoney, parseMoney, toApiAmount } from '../lib/money'
import { type Edits, type History as SheetHistory, prune, SAVE_CAP } from '../lib/sheet'
import { useSession } from '../lib/session'
import { headCell as sharedHeadCell } from '../components/DataTable'
import { ViewHeader } from '../shell/ViewHeader'

/**
 * **Revenue** — `APPLICATION_SPEC.md` §5.1, SOW workstream 5.
 *
 * Revenue is the only data in this app with no upstream to re-derive it from.
 * No revenue or monetisation metric exists anywhere in the Snap Public Profile
 * API — confirmed by Snap in writing — and automated retrieval is out of scope
 * **at Snap's own instruction**. This page and the one-off seed import are the
 * only two routes by which a figure ever enters the system.
 *
 * Three views, because SOW workstream 5 buys four things and they are not one
 * table: *"structured form with validation, history imported once, editable by
 * permitted users with a change record."*
 *
 *   1 · **Entry grid** — the sheet-shaped form. Wide, mirroring the workbook,
 *       because Dishant asked us to mimic it as the entry UI.
 *   2 · **History** — the long form, one row per recorded channel-day. What
 *       `/raw-data` will export.
 *   3 · **Change record** — `app.revenue_audit`, the one contractual table.
 *
 * **The single most consequential rule on this page: an empty cell records
 * nothing, and is never a zero.** Verified against the seed workbook — there is
 * not one explicit `0` in it, and its interior blanks are a contiguous
 * 27 Jul–9 Aug recording gap across 8–11 of 12 channels while `Arabic`, which
 * starts 10 Aug, is 100% dense. Those channels were unambiguously earning across
 * that window. So clearing a cell deletes the row; it does not write `0`.
 *
 * **Admin-only** (RV-3, 25 Sep). Every read and write is refused server-side to
 * anyone else; `editable` stays a rendering hint, never the control.
 */

type View = 'grid' | 'history' | 'changes'

const VIEWS = [
  { key: 'grid' as const, label: 'Entry Grid' },
  { key: 'history' as const, label: 'History' },
  { key: 'changes' as const, label: 'Change Record' },
]

// Both long tables grow every day someone enters a figure, and the change record
// grows faster still — it gains a row per edit and never loses one, by design.
// The first render of the imported seed put 658 audit rows on one 13,000px page.
const PAGE_SIZE = 50

// Must match `MAX_RANGE_DAYS` in `api/revenue.py` — one view's widest range.
const MAX_RANGE_DAYS = 730

// Must match `/api/revenue/audit`'s default `limit`. The change record only ever
// grows — it gains a row per edit and never loses one, by design — so the
// endpoint caps what it returns. **The page has to say so:** a title reading
// "500 entries" when 500 is the cap is a wrong number presented as a fact, which
// is the failure mode this project's own rules exist to prevent. Export gives
// the full set.
const AUDIT_LIMIT = 500

/**
 * The entry grid's column heading: **the source workbook's own column name.**
 *
 * ⚠️ This reverses `APPLICATION_SPEC.md` §5.1 decision 2, which said *"Snap
 * display names, not the sheet's names."* Changed on Nilay's call, 8 Sep, and it
 * is the right call for this one screen — the reports keep Snap's names:
 *
 * · **It matches the muscle memory of the person typing.** Whoever enters these
 *   figures does it in that workbook today; `PT France` and `TT Dutch` are the
 *   headings they already know. The grid exists to mimic the sheet.
 * · **It is what makes 13 columns fit.** `PT Dutch` is a third the width of
 *   `Phony Texts NL`, and the layout problem was never really the count.
 * · **Nothing is hidden** — the Snap channel is on hover, and for the four
 *   columns whose mapping is only *corroborated* (Q-C1) the hover is now more
 *   informative than the old heading was, because it names both sides of the
 *   join it is uncertain about.
 *
 * `phonyindia` has **no column in the sheet** (Q-C2), so it falls back to its
 * Snap label — and is marked, because it is the one column with no sheet
 * provenance at all. The odd one out should look odd.
 */
function columnHeading(channel: RevenueGridChannel): {
  label: string
  fromSheet: boolean
} {
  const sheet = channel.revenue_column?.trim()
  return sheet ? { label: sheet, fromSheet: true } : { label: channel.display_label, fromSheet: false }
}

/**
 * The column tooltip: **how this channel is recorded, and nothing else.**
 *
 * Nilay, 10 Sep — *"can we only show data like how it's recorded for Deadzone
 * and Phony Texts, let's not add more information or explanation"*. So it names
 * the sheet column and stops. The confidence caveat that used to be appended
 * here is gone with it.
 */
function headerNote(channel: RevenueGridChannel): string {
  const heading = columnHeading(channel)
  return heading.fromSheet
    ? `Recorded as “${heading.label}” In the Revenue Sheet.`
    : `No Column in the Revenue Sheet.`
}

/** Today in UTC, matching how every date in this app is resolved. */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10)
}

function yesterdayUtc(): string {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - 1)
  return date.toISOString().slice(0, 10)
}

function dayRange(from: string, to: string): string[] {
  const days: string[] = []
  const cursor = new Date(`${from}T00:00:00Z`)
  const end = new Date(`${to}T00:00:00Z`)
  while (cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return days
}

function cellKey(profileId: string, day: string): string {
  return `${profileId}|${day}`
}

export function Revenue() {
  const { me } = useSession()
  const { from, to, period, view, setView, group, channels } = useFilters()
  const active = (VIEWS.some((v) => v.key === view) ? view : 'grid') as View
  // `group === 'all'` and `group === null` both mean the whole estate, and the
  // API takes absence rather than a sentinel. Without this the header's
  // "All 13 channels" and "English · 3 channels" buttons rendered and did
  // nothing on this page — a control that does nothing is worse than no control.
  const scope: LanguageGroup | undefined =
    group === 'english' || group === 'international' ? group : undefined

  // **Revenue's range is one day wider than every other view's, deliberately.**
  // Named periods end *yesterday* because Snap's day is UTC and today is still
  // accumulating (`lib/filters`). That is right for a measured metric and wrong
  // for a hand-typed one, so today's row would otherwise be unreachable without
  // switching to a custom range.
  //
  // It has to widen the **fetch**, not only the rendered rows: a figure saved for
  // today would not come back in a refetch bounded at yesterday, and the input
  // would blank as though the save had been lost.
  //
  // The header still renders the *selected* period from `useFilters()`, which
  // stays truthful — the extra day is labelled in the grid instead.
  // 🔴 **Named periods only.** This was a bare `to === yesterdayUtc()`, which
  // also fired on a *custom* range that happened to end yesterday — so typing an
  // explicit end date silently got you a different one, and the header has
  // nowhere to explain it there (on `custom` it renders two date inputs, not a
  // read-out). An explicit date is now honoured exactly.
  const includesToday = period !== 'custom' && to === yesterdayUtc()
  const slice = {
    from,
    to: includesToday ? todayUtc() : to,
    group: scope,
    channels,
  }

  // Belt and braces with the route guard: a user who reaches this URL without
  // the grant gets a plain answer rather than three failing queries.
  if (me?.role !== 'admin') {
    return (
      <>
        <ViewHeader title="Revenue" scoped={false} />
        <Note tone="warning">Revenue Is Available to Admins Only.</Note>
      </>
    )
  }

  return (
    <>
      <ViewHeader
        title="Revenue"
        // RV-1: the read-out must say what the tables show. All three tabs
        // receive the widened `to`, so all three were a day wider than the
        // header admitted.
        shownTo={slice.to}
        views={VIEWS}
        activeView={active}
        onView={(key) => setView(key)}
        allowLifetime={false}
        // The period always applies — it decides which days exist to type into.
        // *Which channels* does not, on the entry grid: it is a form, and hiding
        // columns risks someone believing a channel is absent. The two report
        // tabs keep the control, which is what spec §5.1 table B asks for.
        channelPicker={active !== 'grid'}
      />

      {/* **The grid ignores the channel filter, because it shows no control for
          it.** A `group=english` left in the URL by the History tab would
          otherwise narrow the grid to three columns with nothing on screen to
          clear — a filter you cannot see is worse than no filter, and on a form
          it would read as "those channels are gone". The date range still
          applies: that control is right there. */}
      {active === 'grid' && (
        <EntryGrid slice={{ from: slice.from, to: slice.to }} />
      )}
      {active === 'history' && <History slice={slice} />}
      {active === 'changes' && <ChangeRecord slice={slice} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// 1 · Entry grid — the sheet-shaped form
// ---------------------------------------------------------------------------

function EntryGrid({ slice }: { slice: RevenueQuery }) {
  const queryClient = useQueryClient()
  const { setRange } = useFilters()
  const query = useQuery({
    queryKey: ['revenue', 'grid', slice],
    queryFn: () => api.revenueGrid(slice),
    // X-16: keep the panel on screen while a range change refetches.
    placeholderData: (previous) => previous,
  })

  /** Only what the user has touched. `''` is a cleared cell. */
  const [edits, setEdits] = useState<Edits>({})
  const [history, setHistory] = useState<SheetHistory>({ past: [], future: [] })
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => {
      const cells = Object.entries(edits).map(([key, raw]) => {
        const separator = key.indexOf('|')
        const profile_id = key.slice(0, separator)
        const revenue_date = key.slice(separator + 1)
        // An empty cell clears it — the API deletes the row. This is the one
        // place a UI decision would silently become a fabricated zero, so the
        // conversion lives in `lib/money` where it is tested.
        return { profile_id, revenue_date, amount: toApiAmount(raw) }
      })
      return api.saveRevenue(cells)
    },
    onSuccess: () => {
      // Undo covers unsaved edits only: after a save the change record is the
      // history, and a correction is a new edit.
      setEdits({})
      setHistory({ past: [], future: [] })
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['revenue'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save Failed'),
  })

  if (query.isLoading) return <Skeleton rows={8} />
  if (!query.data) return <Note tone="warning">Could Not Load Revenue.</Note>

  const grid = query.data
  const today = todayUtc()
  // **How new rows appear as dates pass.** They are generated here from the
  // range the server echoes back, and named periods are *derived* rather than
  // frozen — so `Last 30 days` moves forward on its own. Rows exist in
  // `app.revenue` only where a figure was actually recorded.
  const days = dayRange(grid.date_from, grid.date_to)
  const stored = new Map(
    grid.cells.map((cell) => [cellKey(cell.profile_id, cell.revenue_date), cell.amount]),
  )
  const storedAt = (profileId: string, day: string) =>
    formatMoney(stored.get(cellKey(profileId, day)))

  // Staging a value equal to the stored one is not a change — it would tint
  // the cell, count against the save and write an audit row saying nothing.
  const setPruned = (next: Edits) =>
    setEdits(
      prune(next, (key, value) => parseMoney(value) === parseMoney(formatMoney(stored.get(key)))),
    )

  const dirtyCount = Object.keys(edits).length

  // RV-6: per-day totals, including unsaved edits, so the chart moves as Jesse
  // types. Only days with a figure are in the map — a blank is not zero.
  const byDay = new Map<string, number>()
  for (const day of days) {
    let total = 0
    let any = false
    for (const channel of grid.channels) {
      const key = cellKey(channel.profile_id, day)
      const value = parseMoney(edits[key] ?? formatMoney(stored.get(key)))
      if (value !== null) {
        total += value
        any = true
      }
    }
    if (any) byDay.set(day, total)
  }

  // RV-7: jump the range to a month, or to everything recorded. Both are custom
  // ranges, capped at the API's 730 days and never past today.
  const jumpToMonth = (month: string) => {
    if (!/^\d{4}-\d{2}$/.test(month)) return
    const [year, m] = month.split('-').map(Number) as [number, number]
    const last = new Date(Date.UTC(year, m, 0)).toISOString().slice(0, 10)
    setRange(`${month}-01`, last > today ? today : last)
  }
  const allRecorded = () => {
    if (!grid.earliest_recorded) return
    const cap = new Date(`${today}T00:00:00Z`)
    cap.setUTCDate(cap.getUTCDate() - (MAX_RANGE_DAYS - 1))
    const floor = cap.toISOString().slice(0, 10)
    setRange(grid.earliest_recorded > floor ? grid.earliest_recorded : floor, today)
  }
  const overCap =
    grid.earliest_recorded !== null &&
    grid.earliest_recorded !== undefined &&
    dayRange(grid.earliest_recorded, today).length > MAX_RANGE_DAYS

  return (
    <div className="flex flex-col gap-4">
      <RevenueChart days={days} byDay={byDay} currency={grid.currency} />

      <Card
        title={`Entry Grid · ${grid.currency}`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-label)' }}>
              Jump to
              <input
                type="month"
                aria-label="Jump to Month"
                max={today.slice(0, 7)}
                defaultValue={grid.date_from.slice(0, 7)}
                onChange={(event) => jumpToMonth(event.target.value)}
                className="px-1 py-0.5 text-xs"
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                }}
              />
            </label>
            <Button
              variant="quiet"
              onClick={allRecorded}
              disabled={!grid.earliest_recorded}
              title={
                overCap
                  ? `Everything Recorded Is More Than ${MAX_RANGE_DAYS} Days; Shows the Latest ${MAX_RANGE_DAYS}.`
                  : 'From the First Recorded Day to Today'
              }
            >
              All Recorded
            </Button>
            {/* RV-4: above the grid, shaped exactly as it is on screen. */}
            <DownloadButton
              url={api.revenueGridExportUrl({ from: grid.date_from, to: grid.date_to })}
              filename={`revenue_grid_${grid.date_from}_${grid.date_to}.csv`}
              className="px-3 py-1.5 text-sm font-medium"
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
              }}
            >
              Export CSV
            </DownloadButton>
            {grid.editable && dirtyCount > 0 && (
              <>
                <Button
                  onClick={() => {
                    setEdits({})
                    setHistory({ past: [], future: [] })
                    setError(null)
                  }}
                >
                  Discard {dirtyCount}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => save.mutate()}
                  disabled={save.isPending || dirtyCount > SAVE_CAP}
                >
                  {save.isPending
                    ? 'Saving…'
                    : `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`}
                </Button>
              </>
            )}
          </div>
        }
      >
        {error && (
          <div className="mb-3">
            <Note tone="warning">{error}</Note>
          </div>
        )}
        <RevenueSheet
          channels={grid.channels}
          days={days}
          today={today}
          storedAt={storedAt}
          edits={edits}
          onEdits={setPruned}
          onError={setError}
          history={history}
          onHistory={setHistory}
          editable={grid.editable}
          headerFor={(channel) => (
            <Hint label={headerNote(channel)}>
              <span
                style={{
                  cursor: 'help',
                  // The one column with no sheet provenance reads as borrowed.
                  fontStyle: columnHeading(channel).fromSheet ? undefined : 'italic',
                }}
              >
                {columnHeading(channel).label}
              </span>
            </Hint>
          )}
        />
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 2 · History — the long form
// ---------------------------------------------------------------------------

function History({ slice }: { slice: RevenueQuery }) {
  const [page, setPage] = useState(0)
  const query = useQuery({
    queryKey: ['revenue', 'history', slice],
    queryFn: () => api.revenueHistory(slice),
    // X-16: keep the panel on screen while a range change refetches. The header
    // survives here (it is rendered by the page, not this component), so the
    // symptom is a flash rather than the whole page going — but it is the same
    // cause and the same one-line remedy.
    placeholderData: (previous) => previous,
  })

  if (query.isLoading) return <Skeleton rows={8} />
  const rows = query.data ?? []
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const shown = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <Card
      title={`Revenue History · ${rows.length} recorded channel-days`}
      action={
        <a
          href={api.revenueExportUrl({ ...slice, dataset: 'revenue' })}
          className="text-xs underline"
          style={{ color: 'var(--mint-700)' }}
        >
          Export CSV
        </a>
      }
      footer="Rows Exist Only Where a Figure Was Recorded. A Missing Channel-Day Is Absent, Not Zero."
    >
      {rows.length === 0 ? (
        <Note>No Revenue Recorded in This Range.</Note>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Channel', 'Date', 'Amount', 'Source', 'Entered By', 'Last Updated'].map(
                  (header, index) => (
                    <th
                      key={header}
                      style={headCell}
                      className={index >= 2 && index <= 2 ? 'text-right' : 'text-left'}
                    >
                      {header}
                      {header === 'Source' && (
                        <InfoDot note="`import` is the one-off seed load from the client’s workbook; `manual` is typed in this app." />
                      )}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {shown.map((row: RevenueHistoryRow) => (
                <tr key={`${row.profile_id}-${row.revenue_date}`}>
                  <td style={bodyCell}>{row.display_label}</td>
                  <td style={{ ...bodyCell, ...numberStyle }}>
                    {formatDate(row.revenue_date)}
                  </td>
                  <td style={{ ...bodyCell, ...numberStyle }} className="text-right">
                    {formatMoney(Number(row.amount))} {row.currency}
                  </td>
                  <td style={bodyCell}>{row.source}</td>
                  <td style={{ ...bodyCell, color: 'var(--text-label)' }}>{row.entered_by ?? '—'}</td>
                  <td style={{ ...bodyCell, ...numberStyle, color: 'var(--text-label)' }}>
                    {/* RV-2: a string slice standing in for a formatter. */}
                    {formatDate(row.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageCount={pageCount}
            rowCount={rows.length}
            onChange={setPage}
          />
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// 3 · Change record — SOW §02's "record of what changed and when"
// ---------------------------------------------------------------------------

function ChangeRecord({ slice }: { slice: RevenueQuery }) {
  const [page, setPage] = useState(0)
  const query = useQuery({
    queryKey: ['revenue', 'audit', slice],
    queryFn: () => api.revenueAudit(slice),
    // X-16: keep the panel on screen while a range change refetches. The header
    // survives here (it is rendered by the page, not this component), so the
    // symptom is a flash rather than the whole page going — but it is the same
    // cause and the same one-line remedy.
    placeholderData: (previous) => previous,
  })

  if (query.isLoading) return <Skeleton rows={8} />
  const rows = query.data ?? []
  const capped = rows.length >= AUDIT_LIMIT
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const shown = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <Card
      title={
        capped
          ? `Change Record · Most Recent ${AUDIT_LIMIT} of more`
          : `Change Record · ${rows.length} entries`
      }
      action={
        <a
          href={api.revenueExportUrl({ ...slice, dataset: 'revenue_audit' })}
          className="text-xs underline"
          style={{ color: 'var(--mint-700)' }}
        >
          Export CSV
        </a>
      }
    >
      {rows.length === 0 ? (
        <Note>No Changes Recorded in This Range.</Note>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headCell} className="text-left">Changed at</th>
                <th style={headCell} className="text-left">Changed by</th>
                <th style={headCell} className="text-left">Channel</th>
                <th
                  style={{ ...headCell, ...stickyLeft, ...stickyCorner }}
                  className="text-left"
                >
                  Date
                </th>
                <th style={headCell} className="text-left">Action</th>
                <th style={headCell} className="text-right">Old value</th>
                <th style={headCell} className="text-right">New value</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row: RevenueAuditRow, index) => (
                <tr key={`${row.changed_at}-${row.profile_id}-${index}`}>
                  <td style={{ ...bodyCell, ...numberStyle, color: 'var(--text-label)' }}>
                    {row.changed_at.slice(0, 19).replace('T', ' ')}
                  </td>
                  <td style={bodyCell}>{row.changed_by ?? '—'}</td>
                  <td style={bodyCell}>{row.display_label ?? row.profile_id}</td>
                  <td style={{ ...bodyCell, ...numberStyle }}>
                    {formatDate(row.revenue_date)}
                  </td>
                  <td style={bodyCell}>{row.action}</td>
                  <td style={{ ...bodyCell, ...numberStyle }} className="text-right">
                    {/* Empty on `created`, per spec §5.1 — not 0. */}
                    {row.old_amount === null ? (
                      <span style={{ color: 'var(--text-label)' }}>—</span>
                    ) : (
                      formatMoney(Number(row.old_amount))
                    )}
                  </td>
                  <td style={{ ...bodyCell, ...numberStyle }} className="text-right">
                    {/* Empty on `deleted`. */}
                    {row.new_amount === null ? (
                      <span style={{ color: 'var(--text-label)' }}>—</span>
                    ) : (
                      formatMoney(Number(row.new_amount))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageCount={pageCount}
            rowCount={rows.length}
            onChange={setPage}
          />
        </div>
      )}
    </Card>
  )
}

// Tokens only — never a hex, a px spacing value, a radius or a shadow written
// into a component (`docs/DESIGN-SYSTEM.md` §3).
/**
 * The header row stays visible while you scroll the grid.
 *
 * **This has to be an inner scroll box, and that is forced rather than
 * preferred.** Measured in the browser: the table's container carries
 * `overflow-x: auto` — the house rule for wide content, since the body must never
 * scroll sideways — and per CSS, `overflow-y: visible` *computes to `auto`* when
 * the other axis is not visible. So the container is already a scroll container
 * on both axes, and a `position: sticky` header inside it pins to that container,
 * never to the viewport. With no height limit it had nothing to pin to and
 * scrolled away (`top: -432px` after a 900px scroll). Viewport-sticky headers and
 * a horizontal scroll container cannot coexist.
 *
 * So the container gets a height, which makes the sticky header work.
 *
 * ⚠️ **This app removed inner vertical scroll once, for good reason:** every
 * table sat in a fixed-height box and 490 unpaginated Channel Daily rows
 * *swallowed the wheel entirely* — `scrollY` stuck across six wheel events, so
 * the page read as frozen (`EXECUTION-PLAN.md`, 8 Sep). Two things keep that from
 * recurring here:
 *
 * · **The box is short, so it is crossed in a few wheel events rather than
 *   hundreds.** Measured at a 1440x900 viewport: the default 30-day grid takes
 *   **3 wheel events** to reach the end, after which the page scrolls normally.
 *   The defect was a *fixed* box holding 490 rows, where the wheel never got
 *   out. (It does bind at the default range on a 900px-tall screen — the grid is
 *   ~1063px — which is precisely when a pinned header earns its keep.)
 * · **`overscroll-behavior` is left at its default**, so the wheel chains to the
 *   page the moment the box reaches its end — verified, `scrollY` moves off 0 on
 *   the fourth wheel event. Setting `contain` here would reproduce the defect
 *   exactly.
 */
/** The shared header contract (X-11), plus the two things only this grid needs:
 *  it is a sticky, scrollable form, so its headers must stay opaque and above
 *  the rows sliding under them. Everything about how a header *reads* comes
 *  from `DataTable`. */
const headCell = {
  ...sharedHeadCell,
  position: 'sticky',
  top: 0,
  // Above the sticky body cells, below the shell's sidebar and any popover.
  zIndex: 3,
  // Opaque: sticky cells sit over the rows sliding under them.
  background: 'var(--bg-surface)',
} as const

// The entry grid's own sticky styles live in `components/RevenueSheet.tsx`;
// these remain for the History and Change Record tables.
const stickyLeft = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: 'var(--bg-surface)',
} as const

/** A cell sticky on both axes has to out-rank each of them separately. */
const stickyCorner = { zIndex: 4 } as const

const bodyCell = {
  padding: 'var(--space-1) var(--space-2)',
  borderBottom: '1px solid var(--border-subtle, var(--border))',
  color: 'var(--text-primary)',
  whiteSpace: 'nowrap',
} as const

export type { RevenueGrid, RevenueGridChannel }
